import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, toast } from '@/App';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function FullLengthTests({ user, setUser }) {
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [diagnosticTest, setDiagnosticTest] = useState(null);

  // Set page title for analytics
  useEffect(() => {
    document.title = 'Full Length Tests | Practice4SAT';
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [testsRes, analyticsRes, attemptsRes, diagnosticRes] = await Promise.all([
        axios.get(`${API}/tests`),
        axios.get(`${API}/analytics/dashboard`),
        axios.get(`${API}/test-attempts/all`),
        axios.get(`${API}/diagnostic-status`).catch(() => ({ data: null }))
      ]);
      
      // Map test attempts to tests
      const testsWithScores = (testsRes.data || []).map(test => {
        const attempts = (attemptsRes.data || []).filter(att => att.test_id === test.id);
        const latestAttempt = attempts.length > 0 ? attempts[0] : null;
        return {
          ...test,
          latestScore: latestAttempt?.score,
          latestRwScore: latestAttempt?.rw_score,
          latestMathScore: latestAttempt?.math_score,
          latestSessionId: latestAttempt?.session_id,
          attemptCount: attempts.length
        };
      });
      
      setTests(testsWithScores);
      setAnalytics(analyticsRes.data);
      setDiagnosticTest(diagnosticRes.data);
    } catch (error) {
      console.error('Dashboard fetch error:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const startTest = (testId) => {
    navigate(`/test/${testId}`, { state: { returnPath: '/tests' } });
  };

  if (loading) {
    return <div className="loading-screen">Loading dashboard...</div>;
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header 
        variant="authenticated" 
        user={user} 
        setUser={setUser} 
        logoClickPath="/dashboard"
        activePage="tests"
      />

      {/* Main Content */}
      <div style={{ flex: 1, background: '#f5f5f5' }}>
        <div className="dashboard-content">
        
        {/* Diagnostic Test Card */}
        <div style={{ marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: '#1a202c', fontFamily: 'Arial, sans-serif' }}>
            SAT Diagnostic Test
          </h2>
          <div className="test-card" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none', color: 'white' }}>
            <div className="test-card-header">
              <h3 style={{ color: 'white', margin: 0, fontFamily: 'Arial, sans-serif' }}>Diagnostic Test</h3>
              {diagnosticTest?.taken ? (
                <Badge style={{ background: 'rgba(255,255,255,0.3)', color: 'white' }}>Completed</Badge>
              ) : (
                <Badge style={{ background: '#10b981', color: 'white' }}>Available</Badge>
              )}
            </div>
            <p style={{ color: 'white', margin: '0.5rem 0 1rem 0' }}>
              {diagnosticTest?.taken 
                ? 'You have completed the diagnostic test. Review your performance below.'
                : 'Take the diagnostic test to assess your current level and get personalized recommendations.'}
            </p>
            {diagnosticTest?.taken ? (
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                {/* Section Score Cards */}
                <div style={{ display: 'flex', gap: '0.75rem', flex: '1 1 200px' }}>
                  {/* R&W Score Card */}
                  <div style={{ 
                    flex: 1, 
                    background: 'rgba(255,255,255,0.2)',
                    borderRadius: '8px',
                    padding: '0.75rem',
                    textAlign: 'center',
                    backdropFilter: 'blur(10px)'
                  }}>
                    <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.9)', marginBottom: '0.25rem', fontWeight: '500' }}>
                      R&W
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'white', lineHeight: 1 }}>
                      {diagnosticTest.rw_score || '—'}
                    </div>
                  </div>

                  {/* Math Score Card */}
                  <div style={{ 
                    flex: 1, 
                    background: 'rgba(255,255,255,0.2)',
                    borderRadius: '8px',
                    padding: '0.75rem',
                    textAlign: 'center',
                    backdropFilter: 'blur(10px)'
                  }}>
                    <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.9)', marginBottom: '0.25rem', fontWeight: '500' }}>
                      Math
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'white', lineHeight: 1 }}>
                      {diagnosticTest.math_score || '—'}
                    </div>
                  </div>

                  {/* Total Score */}
                  <div style={{ 
                    flex: 1, 
                    background: 'rgba(255,255,255,0.3)',
                    borderRadius: '8px',
                    padding: '0.75rem',
                    textAlign: 'center',
                    backdropFilter: 'blur(10px)'
                  }}>
                    <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.9)', marginBottom: '0.25rem', fontWeight: '500' }}>
                      Total
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'white', lineHeight: 1 }}>
                      {diagnosticTest.final_score}
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '0.5rem', marginLeft: 'auto' }}>
                  <Button 
                    onClick={() => navigate(`/analytics/${diagnosticTest.session_id}`)}
                    variant="outline"
                    style={{ background: 'white', color: '#667eea', border: 'none' }}
                  >
                    View Results
                  </Button>
                  <Button 
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('token');
                        const response = await axios.post(`${API}/diagnostic/start`, {
                          email: user.email
                        }, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        // Store all session data for full SAT format
                        localStorage.setItem('diagnostic_session_id', response.data.session_id);
                        localStorage.setItem('diagnostic_email', user.email);
                        localStorage.setItem('diagnostic_questions', JSON.stringify(response.data.questions));
                        localStorage.setItem('diagnostic_section', response.data.section || 'rw');
                        localStorage.setItem('diagnostic_section_name', response.data.section_name || 'Reading and Writing');
                        localStorage.setItem('diagnostic_time_limit', response.data.time_limit || '1920');
                        navigate('/diagnostic-test');
                      } catch (error) {
                        console.error('Error starting diagnostic:', error);
                        alert('Failed to start diagnostic test');
                      }
                    }}
                    style={{ background: '#10b981', color: 'white' }}
                  >
                    Retake Test
                  </Button>
                </div>
              </div>
            ) : (
              <Button 
                onClick={async () => {
                  try {
                    // Get auth token
                    const token = localStorage.getItem('token');
                    
                    // Start diagnostic test for logged-in user
                    const response = await axios.post(
                      `${API}/diagnostic/start`, 
                      { email: user.email },
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    
                    // Store session data for full SAT format
                    localStorage.setItem('diagnostic_session_id', response.data.session_id);
                    localStorage.setItem('diagnostic_email', user.email);
                    localStorage.setItem('diagnostic_questions', JSON.stringify(response.data.questions));
                    localStorage.setItem('diagnostic_section', response.data.section || 'rw');
                    localStorage.setItem('diagnostic_section_name', response.data.section_name || 'Reading and Writing');
                    localStorage.setItem('diagnostic_time_limit', response.data.time_limit || '1920');
                    
                    // Navigate to test
                    navigate('/diagnostic-test');
                  } catch (error) {
                    console.error('Error starting diagnostic test:', error);
                    toast.error('Failed to start diagnostic test');
                  }
                }}
                style={{ background: 'white', color: '#667eea' }}
              >
                Start Diagnostic Test
              </Button>
            )}
          </div>
        </div>

        {/* Mock Tests Section */}
        <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: '#1a202c', fontFamily: 'Arial, sans-serif' }}>
          SAT Adaptive Tests
        </h2>

        <div className="tests-section">
          <div className="tests-grid">
            {tests.slice(0, 8).map((test, idx) => {
              const testId = test?.id;
              const testTitle = test?.title || test?.name || 'Unknown Test';
              return (
              <div key={testId || idx} className="test-card" style={{ display: 'flex', flexDirection: 'column', minHeight: '280px' }} data-testid={`test-card-${idx}`}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.75rem' }}>
                  <h3 style={{ margin: 0 }}>{testTitle}</h3>
                </div>
                
                {/* Score Display - Flex grow to push buttons down */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '140px' }}>
                  {test.latestScore ? (
                    <>
                      {/* Section Score Cards */}
                      <div style={{ display: 'flex', gap: '0.75rem', width: '100%', marginBottom: '0.75rem' }}>
                        {/* R&W Score Card */}
                        <div style={{ 
                          flex: 1, 
                          background: 'linear-gradient(135deg, #f59e0b, #ef4444)',
                          borderRadius: '8px',
                          padding: '0.75rem',
                          textAlign: 'center'
                        }}>
                          <div style={{ fontSize: '0.75rem', color: 'white', marginBottom: '0.25rem', fontWeight: '600' }}>
                            R&W
                          </div>
                          <div style={{ fontSize: '1.75rem', fontWeight: '700', color: 'white', lineHeight: 1 }}>
                            {test.latestRwScore || '—'}
                          </div>
                        </div>

                        {/* Math Score Card */}
                        <div style={{ 
                          flex: 1, 
                          background: 'linear-gradient(135deg, #10b981, #06b6d4)',
                          borderRadius: '8px',
                          padding: '0.75rem',
                          textAlign: 'center'
                        }}>
                          <div style={{ fontSize: '0.75rem', color: 'white', marginBottom: '0.25rem', fontWeight: '600' }}>
                            Math
                          </div>
                          <div style={{ fontSize: '1.75rem', fontWeight: '700', color: 'white', lineHeight: 1 }}>
                            {test.latestMathScore || '—'}
                          </div>
                        </div>
                      </div>

                      {/* Total Score */}
                      <div style={{ textAlign: 'center' }}>
                        <span style={{ fontSize: '0.8rem', color: '#666' }}>Total: </span>
                        <span style={{ fontSize: '1.1rem', fontWeight: '700', color: '#22c55e' }} data-testid={`test-score-${idx}`}>
                          {test.latestScore}
                        </span>
                        <span style={{ fontSize: '0.8rem', color: '#666' }}> / 1600</span>
                      </div>

                      {test.attemptCount >= 1 && (
                        <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.5rem' }}>
                          Attempts: {test.attemptCount}
                        </div>
                      )}
                    </>
                  ) : (
                    <div style={{ textAlign: 'center', color: '#666', fontSize: '0.95rem' }}>
                      No score yet
                    </div>
                  )}
                </div>
                
                {/* Buttons at bottom */}
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: 'auto', paddingTop: '0.75rem' }}>
                  {test.latestSessionId ? (
                    <>
                      <Button 
                        onClick={() => navigate(`/analytics/${test.latestSessionId}`)} 
                        className="flex-1"
                        variant="outline"
                        data-testid={`view-analytics-btn-${idx}`}
                      >
                        View Results
                      </Button>
                      <Button 
                        onClick={() => startTest(testId)} 
                        className="flex-1"
                        data-testid={`retake-test-btn-${idx}`}
                      >
                        Retake Test
                      </Button>
                    </>
                  ) : (
                    <Button 
                      onClick={() => startTest(testId)} 
                      className="w-full font-bold"
                      style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)' }}
                      data-testid={`start-test-btn-${idx}`}
                    >
                      Start Test
                    </Button>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        </div>
      </div>
      </div>

      <Footer />
    </div>
  );
}
